import React from 'react';
import { Card as MuiCard, CardContent, CardHeader as MuiCardHeader, CardActions, Box, Typography, IconButton } from '@mui/material';
import { MoreVertical } from 'lucide-react';
import { useTheme } from '@mui/material/styles';

/**
 * UnifiedCard - Consolidated card component with theme-based styling
 * 
 * Supports variants:
 * - default: Standard card with subtle shadow
 * - elevated: Card with strong shadow
 * - outlined: Card with border, no shadow
 * - glass: Frosted glass effect
 * - gradient: Gradient background using theme colors
 * 
 * Replaces: EnhancedCard.jsx variants (GlassCard, GradientCard, ElevatedCard, BorderedCard, InteractiveCard)
 */
export const UnifiedCard = ({ 
  children, 
  variant = 'default',
  hover = true,
  gradient = 'primary', // primary, secondary, success, warning, error, info
  interactive = false,
  ...props 
}) => {
  const theme = useTheme();

  // Get gradient from theme colors
  const getGradient = () => {
    const gradients = {
      primary: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
      secondary: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
      success: `linear-gradient(135deg, ${theme.palette.success.light} 0%, ${theme.palette.success.main} 100%)`,
      warning: `linear-gradient(135deg, ${theme.palette.warning.light} 0%, ${theme.palette.warning.main} 100%)`,
      error: `linear-gradient(135deg, ${theme.palette.error.light} 0%, ${theme.palette.error.main} 100%)`,
      info: `linear-gradient(135deg, ${theme.palette.info.light} 0%, ${theme.palette.info.main} 100%)`,
    };
    return gradients[gradient] || gradients.primary;
  };

  const getSx = () => {
    const baseSx = {
      borderRadius: 2,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
    };

    // Hover effect
    if (hover) {
      baseSx['&:hover'] = {
        transform: 'translateY(-4px)',
        boxShadow: 8,
      };
    }

    // Variant-specific styles
    switch (variant) {
      case 'elevated':
        return {
          ...baseSx,
          boxShadow: '0 10px 30px -5px rgba(0, 0, 0, 0.1)',
        };

      case 'outlined':
        return {
          ...baseSx,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        };

      case 'glass':
        return {
          ...baseSx,
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(15px)',
          WebkitBackdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
        };

      case 'gradient':
        return {
          ...baseSx,
          background: getGradient(),
          backgroundSize: '200% 200%',
          color: theme.palette.primary.contrastText,
          '& .MuiTypography-root': {
            color: theme.palette.primary.contrastText,
          },
          '&:hover': {
            backgroundPosition: 'right center',
            transform: 'translateY(-4px)',
            boxShadow: 8,
          },
        };

      case 'interactive':
        return {
          ...baseSx,
          cursor: 'pointer',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
            opacity: 0,
            transition: 'opacity 0.3s ease, transform 0.3s ease',
            pointerEvents: 'none',
          },
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 8,
          },
          '&:hover::after': {
            opacity: 1,
            transform: 'translate(25%, 25%)',
          },
        };

      default: // default variant
        return {
          ...baseSx,
          boxShadow: 2,
        };
    }
  };

  return (
    <MuiCard sx={getSx()} {...props}>
      {children}
    </MuiCard>
  );
};

/**
 * CardHeader - Reusable card header with title, subtitle, and optional actions
 */
export const CardHeader = ({ title, subtitle, action, onAction }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && (
        <IconButton size="small" onClick={onAction}>
          <MoreVertical size={20} />
        </IconButton>
      )}
    </Box>
  );
};

/**
 * CardFooter - Reusable card footer with actions
 */
export const CardFooter = ({ children }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mt: 2,
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      {children}
    </Box>
  );
};

/**
 * IconWrapper - Reusable icon container with theme-based colors
 */
const IconWrapper = ({ icon, color = 'primary', size = 56, hover = false }) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${color}.main`,
        color: theme.palette.primary.contrastText,
        flexShrink: 0,
        transition: hover ? 'transform 0.2s ease' : 'none',
        ...(hover && {
          '&:hover': {
            transform: 'scale(1.1) rotate(5deg)',
          },
        }),
      }}
    >
      {icon}
    </Box>
  );
};

/**
 * StatCardContent - Reusable stat card content component
 * Used by both UnifiedCard and standalone StatsCard
 */
export const StatCardContent = ({
  icon,
  title,
  value,
  trend,
  trendDirection = 'up',
  subtitle,
  color = 'primary',
  variant = 'default',
}) => {
  const theme = useTheme();
  const trendColor = trendDirection === 'up' ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
      {icon && <IconWrapper icon={icon} color={color} hover />}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Typography variant="caption" sx={{ color: trendColor, fontWeight: 600, mt: 0.5 }}>
            {trendDirection === 'up' ? '↑' : '↓'} {trend}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Export all components
export default UnifiedCard;
